import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import * as React from 'react';
import { useMyServiceContext } from '..';
import { useAppSelector as useSelector } from 'Store/index';
import { Container, Item } from 'Components/Grid';
import { phone } from 'phone';
import { validate } from '@stellarq/email';
import TextField from 'Components/InputHandler/components/TextField';
import { isNumber } from 'neuro-utils';

const InviteForm = ({
  inviteContactInfo,
  setInviteContactInfo,
  onChangeInviteContactInfo,
}: IInviteForm): React.JSX.Element => {
  const { fm, editing } = useMyServiceContext<'_invite'>();

  const patientContactInfo = useSelector(s => s.patient.contactInfo);

  React.useEffect(() => {
    if (editing) {
      setInviteContactInfo(patientContactInfo?.contacts ?? []);
    }
    return () => {
      setInviteContactInfo([]);
    };
  }, []);

  return (
    <React.Fragment>
      <FormRow title="myService.phoneNumber">
        <Container>
          <Item xs={5}>
            <InputHandler
              type="SingleValueList"
              name="smsList"
              editing={!!editing}
              value={inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value)}
              sortMethod={(a, b) => {
                const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                return (
                  (aRank || aRank === 0 ? aRank : Number.MAX_SAFE_INTEGER) -
                  (bRank || bRank === 0 ? bRank : Number.MAX_SAFE_INTEGER)
                );
              }}
              formData={{
                onChange: onChangeInviteContactInfo,
                document: {
                  smsList: inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value),
                },
              }}
              disableSaveButton={value => {
                if (typeof value === 'string') return value.length === 0 || !phone(value, { country: 'FIN' }).isValid;
                return false;
              }}
              disabledTooltip={fm('profile.notValidSms')}
              disableDeletion={value => !!patientContactInfo?.contacts?.some(ci => ci.value === value)}
              component={props => <TextField {...props} type="TextField" placeholder={undefined} />}
            />
          </Item>
        </Container>
      </FormRow>
      <FormRow title="myService.emailAddress">
        <Container>
          <Item xs={5}>
            <InputHandler
              type="SingleValueList"
              name="emailList"
              editing={!!editing}
              value={inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value)}
              sortMethod={(a, b) => {
                const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                return (
                  (isNumber(aRank) ? aRank : Number.MAX_SAFE_INTEGER) -
                  (isNumber(bRank) ? bRank : Number.MAX_SAFE_INTEGER)
                );
              }}
              formData={{
                onChange: onChangeInviteContactInfo,
                document: {
                  emailList: inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value),
                },
              }}
              disableSaveButton={value => {
                if (typeof value === 'string') return value.length === 0 || !validate(value);
                return false;
              }}
              disabledTooltip={fm('profile.notValidEmail')}
              disableDeletion={value => {
                return !!patientContactInfo?.contacts?.some(ci => ci.value === value);
              }}
              component={props => {
                return <TextField {...props} type="TextField" placeholder={undefined} />;
              }}
            />
          </Item>
        </Container>
      </FormRow>
    </React.Fragment>
  );
};

interface IInviteForm {
  inviteContactInfo: (INeuroContactInfo | INeuroUIContactInfo)[] | undefined;
  setInviteContactInfo: React.Dispatch<React.SetStateAction<(INeuroContactInfo | INeuroUIContactInfo)[] | undefined>>;
  onChangeInviteContactInfo: (values: TOnChangeValues) => void;
}

export default InviteForm;
